import { BillingDetails } from '@app/domain/BillingDetails'
import { BillingService } from '@services/billing.service'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Observable, Subject, combineLatest, finalize, map, shareReplay, takeUntil, tap } from 'rxjs'
import { Ability, PermissionService } from '@app/services/permission.service'
import { UserService } from '@app/services/user.service'

interface SubscriptionState {
    billingDetails: BillingDetails
    hasManageBillingPermission: boolean
}

@Component({
    selector: 'app-subscription-banner',
    templateUrl: './subscription-banner.component.html',
    styleUrls: ['./subscription-banner.component.sass'],
})
export class SubscriptionBannerComponent implements OnInit, OnDestroy {

    state$: Observable<SubscriptionState>
    checkoutUrl: string | null = null
    checkoutUrlError = false
    checkoutUrlLoading = false
    private checkoutUrlRequestStarted = false
    private onDestroy$ = new Subject<void>()

    constructor(
        private billingService: BillingService,
        private permissionService: PermissionService,
        private userService: UserService
    ) {
        this.state$ = this.getSubscriptionState()
    }

    ngOnInit() { }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    upgradeTrialClicked() {
        this.billingService.openCheckoutTrialPortal(
            () => {
                this.state$ = this.getSubscriptionState()
            }
        )
    }

    checkoutClicked() {
        if (this.checkoutUrl === null) {
            return
        }
        this.billingService.openTrialSubscriptionCheckoutUrl(this.checkoutUrl)
    }

    retryCheckoutUrlPreload(state: SubscriptionState) {
        this.checkoutUrlRequestStarted = false
        this.preloadCheckoutUrlIfNeeded(state)
    }

    private getSubscriptionState(): Observable<SubscriptionState> {
        const billingDetails$ = this.getBillingDetails()
        const hasManageBillingPermission$ = this.userService.get()
            .pipe(
                map(user => this.permissionService.doesUserHaveAbility(user, Ability.ManageBilling)),
                shareReplay(1)
            )

        return combineLatest([
            billingDetails$,
            hasManageBillingPermission$,
        ])
            .pipe(
                map(([billingDetails, hasManageBillingPermission]) => ({
                    billingDetails,
                    hasManageBillingPermission,
                })),
                tap(state => {
                    this.preloadCheckoutUrlIfNeeded(state)
                }),
                shareReplay(1)
            )
    }

    private getBillingDetails(): Observable<BillingDetails> {
        return this.billingService.getBillingDetails()
            .pipe(shareReplay(1))
    }

    private preloadCheckoutUrlIfNeeded(state: SubscriptionState) {
        if (
            state.billingDetails.inService
            || !state.hasManageBillingPermission
            || this.checkoutUrlRequestStarted
            || this.checkoutUrl !== null
        ) {
            return
        }
        this.checkoutUrlRequestStarted = true
        this.checkoutUrlLoading = true
        this.checkoutUrlError = false
        this.billingService.getTrialSubscriptionCheckoutUrl()
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => {
                    this.checkoutUrlLoading = false
                })
            )
            .subscribe({
                next: checkoutUrl => {
                    this.checkoutUrl = checkoutUrl
                },
                error: () => {
                    this.checkoutUrlError = true
                    this.checkoutUrlRequestStarted = false
                },
            })
    }
}
